import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/feature/LanguageSwitcher';
import CaseBlock from './components/CaseBlock';

export default function CaseStudyPage() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '導入事例 | 株式会社LIKES - 福岡市 組織風土改革 JA 農業協同組合 コンサルティング';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '株式会社LIKESの導入事例。大規模JA（農業協同組合）様の組織風土改革と新規事業創出プロジェクト。課題・解決アプローチ・成果を詳しくご紹介します。福岡市の組織開発コンサルティング実績。');
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/case-study`);
    }
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemas = [
      { id: 'schema-webpage-cs2', data: { '@context': 'https://schema.org', '@type': 'WebPage', url: `${siteUrl}/case-study`, name: '導入事例 | 株式会社LIKES', inLanguage: 'ja' } },
      { id: 'schema-breadcrumb-cs2', data: { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'ホーム', item: `${siteUrl}/` }, { '@type': 'ListItem', position: 2, name: '導入事例', item: `${siteUrl}/case-study` }] } },
    ];
    schemas.forEach(({ id, data }) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      script.id = id;
      document.head.appendChild(script);
    });
    return () => { schemas.forEach(({ id }) => { document.getElementById(id)?.remove(); }); };
  }, []);

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const overviewItems = [
    { icon: 'ri-building-2-line', labelKey: 'page.caseStudy.overview.client', valueKey: 'page.caseStudy.overview.clientVal' },
    { icon: 'ri-calendar-line', labelKey: 'page.caseStudy.overview.period', valueKey: 'page.caseStudy.overview.periodVal' },
    { icon: 'ri-team-line', labelKey: 'page.caseStudy.overview.scale', valueKey: 'page.caseStudy.overview.scaleVal' },
    { icon: 'ri-map-pin-line', labelKey: 'page.caseStudy.overview.region', valueKey: 'page.caseStudy.overview.regionVal' },
  ];

  const challenges = [
    { titleKey: 'page.caseStudy.challenge.1.title', descKey: 'page.caseStudy.challenge.1.desc' },
    { titleKey: 'page.caseStudy.challenge.2.title', descKey: 'page.caseStudy.challenge.2.desc' },
    { titleKey: 'page.caseStudy.challenge.3.title', descKey: 'page.caseStudy.challenge.3.desc' },
  ];

  const approaches = [
    { step: '01', titleKey: 'page.caseStudy.approach.1.title', descKey: 'page.caseStudy.approach.1.desc' },
    { step: '02', titleKey: 'page.caseStudy.approach.2.title', descKey: 'page.caseStudy.approach.2.desc' },
    { step: '03', titleKey: 'page.caseStudy.approach.3.title', descKey: 'page.caseStudy.approach.3.desc' },
  ];

  const resultItems = [
    { titleKey: 'page.caseStudy.results.1.title', descKey: 'page.caseStudy.results.1.desc' },
    { titleKey: 'page.caseStudy.results.2.title', descKey: 'page.caseStudy.results.2.desc' },
    { titleKey: 'page.caseStudy.results.3.title', descKey: 'page.caseStudy.results.3.desc' },
  ];

  const kpis = [
    { valueKey: 'page.caseStudy.results.kpi1.value', labelKey: 'page.caseStudy.results.kpi1.label' },
    { valueKey: 'page.caseStudy.results.kpi2.value', labelKey: 'page.caseStudy.results.kpi2.label' },
    { valueKey: 'page.caseStudy.results.kpi3.value', labelKey: 'page.caseStudy.results.kpi3.label' },
  ];

  const phases = [
    { phaseKey: 'page.caseStudy.timeline.phase1.phase', periodKey: 'page.caseStudy.timeline.phase1.period', titleKey: 'page.caseStudy.timeline.phase1.title', descKey: 'page.caseStudy.timeline.phase1.desc', align: 'left' },
    { phaseKey: 'page.caseStudy.timeline.phase2.phase', periodKey: 'page.caseStudy.timeline.phase2.period', titleKey: 'page.caseStudy.timeline.phase2.title', descKey: 'page.caseStudy.timeline.phase2.desc', align: 'right' },
    { phaseKey: 'page.caseStudy.timeline.phase3.phase', periodKey: 'page.caseStudy.timeline.phase3.period', titleKey: 'page.caseStudy.timeline.phase3.title', descKey: 'page.caseStudy.timeline.phase3.desc', align: 'left' },
    { phaseKey: 'page.caseStudy.timeline.phase4.phase', periodKey: 'page.caseStudy.timeline.phase4.period', titleKey: 'page.caseStudy.timeline.phase4.title', descKey: 'page.caseStudy.timeline.phase4.desc', align: 'right' },
  ];

  const case2Challenges = [
    { titleKey: 'page.caseStudy.case2.challenge.1.title', descKey: 'page.caseStudy.case2.challenge.1.desc' },
  ];

  const case2Approaches = [
    { step: '01', titleKey: 'page.caseStudy.case2.approach.1.title', descKey: 'page.caseStudy.case2.approach.1.desc' },
    { step: '02', titleKey: 'page.caseStudy.case2.approach.2.title', descKey: 'page.caseStudy.case2.approach.2.desc' },
    { step: '03', titleKey: 'page.caseStudy.case2.approach.3.title', descKey: 'page.caseStudy.case2.approach.3.desc' },
  ];

  const case2Results = [
    { titleKey: 'page.caseStudy.case2.results.1.title', descKey: 'page.caseStudy.case2.results.1.desc' },
    { titleKey: 'page.caseStudy.case2.results.2.title', descKey: 'page.caseStudy.case2.results.2.desc' },
  ];

  const case2Kpis = [
    { valueKey: 'page.caseStudy.case2.results.kpi1.value', labelKey: 'page.caseStudy.case2.results.kpi1.label' },
    { valueKey: 'page.caseStudy.case2.results.kpi2.value', labelKey: 'page.caseStudy.case2.results.kpi2.label' },
    { valueKey: 'page.caseStudy.case2.results.kpi3.value', labelKey: 'page.caseStudy.case2.results.kpi3.label' },
  ];

  const case3Challenges = [
    { titleKey: 'page.caseStudy.case3.challenge.1.title', descKey: 'page.caseStudy.case3.challenge.1.desc' },
    { titleKey: 'page.caseStudy.case3.challenge.2.title', descKey: 'page.caseStudy.case3.challenge.2.desc' },
  ];

  const case3Approaches = [
    { step: '01', titleKey: 'page.caseStudy.case3.approach.1.title', descKey: 'page.caseStudy.case3.approach.1.desc' },
    { step: '02', titleKey: 'page.caseStudy.case3.approach.2.title', descKey: 'page.caseStudy.case3.approach.2.desc' },
    { step: '03', titleKey: 'page.caseStudy.case3.approach.3.title', descKey: 'page.caseStudy.case3.approach.3.desc' },
  ];

  const case3Results = [
    { titleKey: 'page.caseStudy.case3.results.1.title', descKey: 'page.caseStudy.case3.results.1.desc' },
    { titleKey: 'page.caseStudy.case3.results.2.title', descKey: 'page.caseStudy.case3.results.2.desc' },
  ];

  const case3Kpis = [
    { valueKey: 'page.caseStudy.case3.results.kpi1.value', labelKey: 'page.caseStudy.case3.results.kpi1.label' },
    { valueKey: 'page.caseStudy.case3.results.kpi2.value', labelKey: 'page.caseStudy.case3.results.kpi2.label' },
    { valueKey: 'page.caseStudy.case3.results.kpi3.value', labelKey: 'page.caseStudy.case3.results.kpi3.label' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none cursor-pointer">
            <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.18em' }} className="text-[#255485] font-bold text-xl sm:text-2xl tracking-widest uppercase">LIKES</span>
            <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.22em' }} className="text-[#255485]/70 text-[8px] sm:text-[9px] font-medium tracking-[0.22em] uppercase mt-0.5">Co, Ltd</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6">
            <Link to="/#vision" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer">{t('nav.vision')}</Link>
            <Link to="/#services" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer">{t('nav.services')}</Link>
            <Link to="/case-study" className="text-[#255485] font-semibold text-sm whitespace-nowrap cursor-pointer border-b-2 border-[#255485] pb-0.5">{t('nav.caseStudy')}</Link>
            <Link to="/#csr" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer">{t('nav.csr')}</Link>
            <Link to="/#company" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer">{t('nav.company')}</Link>
            <LanguageSwitcher />
            <button onClick={scrollToContact} className="bg-[#255485] text-white px-6 py-2.5 rounded-lg hover:bg-[#1d4166] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer">{t('nav.contact')}</button>
          </nav>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
        <div className={`lg:hidden bg-white border-t border-gray-200 transition-all duration-300 overflow-hidden ${mobileMenuOpen ? 'max-h-screen' : 'max-h-0'}`}>
          <nav className="px-4 py-4 space-y-2">
            <div className="flex items-center px-4 py-2 gap-3"><span className="text-xs text-gray-500">Language</span><LanguageSwitcher /></div>
            <Link to="/#vision" onClick={() => setMobileMenuOpen(false)} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] rounded-lg text-sm font-medium cursor-pointer">{t('nav.vision')}</Link>
            <Link to="/#services" onClick={() => setMobileMenuOpen(false)} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] rounded-lg text-sm font-medium cursor-pointer">{t('nav.services')}</Link>
            <Link to="/case-study" onClick={() => setMobileMenuOpen(false)} className="block w-full text-left px-4 py-3 text-[#255485] bg-[#255485]/5 rounded-lg text-sm font-semibold cursor-pointer">{t('nav.caseStudy')}</Link>
            <Link to="/#csr" onClick={() => setMobileMenuOpen(false)} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] rounded-lg text-sm font-medium cursor-pointer">{t('nav.csr')}</Link>
            <Link to="/#company" onClick={() => setMobileMenuOpen(false)} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] rounded-lg text-sm font-medium cursor-pointer">{t('nav.company')}</Link>
            <button onClick={() => { setMobileMenuOpen(false); scrollToContact(); }} className="block w-full text-left px-4 py-3 bg-[#255485] text-white hover:bg-[#1d4166] rounded-lg text-sm font-medium cursor-pointer">{t('nav.contact')}</button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16 sm:pt-20 overflow-hidden">
        <div className="relative h-64 sm:h-80 md:h-96">
          <img src="https://public.readdy.ai/ai/img_res/efbd8b297276a046bc3c28bac9cc4644.jpg" alt="大規模JA 組織風土改革プロジェクト" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-start justify-end pb-10 sm:pb-14 max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm">{t('page.caseStudy.hero.badge')}</span>
              <span className="bg-[#255485]/80 text-white text-xs font-medium px-3 py-1 rounded-full">{t('page.caseStudy.hero.category')}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif text-white leading-snug max-w-3xl" style={{ whiteSpace: 'pre-line' }}>{t('page.caseStudy.hero.title')}</h1>
          </div>
        </div>
      </section>

      {/* Category Tab Switcher */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-center gap-3">
          <Link
            to="/case-study"
            className="px-5 py-2 rounded-full text-sm font-semibold bg-[#255485] text-white whitespace-nowrap cursor-pointer"
          >
            {t('page.caseStudy.tab.consulting')}
          </Link>
          <Link
            to="/case-study/cashless"
            className="px-5 py-2 rounded-full text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('page.caseStudy.tab.cashless')}
          </Link>
        </div>
      </div>

      {/* Overview */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap gap-6 sm:gap-10 justify-center">
            {overviewItems.map((item) => (
              <div key={item.labelKey} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 min-w-[200px]">
                <span className="w-10 h-10 flex items-center justify-center bg-[#255485]/10 rounded-lg text-[#255485]">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <p className="text-xs text-gray-500 font-medium">{t(item.labelKey)}</p>
                  <p className="text-sm text-gray-900 font-semibold mt-0.5">{t(item.valueKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Cards */}
      <section className="py-20 sm:py-28 bg-[#F7F5F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="text-[#255485] text-xs font-semibold tracking-[0.25em] uppercase mb-4">{t('page.caseStudy.project.eyebrow')}</p>
            <h2 className="text-3xl sm:text-4xl font-serif text-gray-900">{t('page.caseStudy.project.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 rounded-2xl overflow-hidden">
            {/* Challenge */}
            <div className="relative bg-[#1C1C1C] text-white p-8 sm:p-10 flex flex-col">
              <span className="absolute top-4 right-6 text-[10rem] font-black leading-none select-none pointer-events-none" style={{ color: 'rgba(255,255,255,0.04)' }}>01</span>
              <div className="mb-8">
                <p className="text-xs font-semibold tracking-[0.2em] text-red-400 uppercase mb-3">{t('page.caseStudy.challenge.eyebrow')}</p>
                <h3 className="text-2xl font-serif text-white leading-snug" style={{ whiteSpace: 'pre-line' }}>{t('page.caseStudy.challenge.title')}</h3>
              </div>
              <div className="flex-1 space-y-0">
                {challenges.map((c, i) => (
                  <div key={c.titleKey} className={`py-5 ${i !== 0 ? 'border-t border-white/10' : ''}`}>
                    <div className="flex items-start gap-3">
                      <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-400"></span>
                      <div>
                        <p className="text-sm font-semibold text-white/90 leading-snug mb-1">{t(c.titleKey)}</p>
                        <p className="text-xs text-white/50 leading-relaxed">{t(c.descKey)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Approach */}
            <div className="relative bg-[#255485] text-white p-8 sm:p-10 flex flex-col">
              <span className="absolute top-4 right-6 text-[10rem] font-black leading-none select-none pointer-events-none" style={{ color: 'rgba(255,255,255,0.06)' }}>02</span>
              <div className="mb-8">
                <p className="text-xs font-semibold tracking-[0.2em] text-white/60 uppercase mb-3">{t('page.caseStudy.approach.eyebrow')}</p>
                <h3 className="text-2xl font-serif text-white leading-snug" style={{ whiteSpace: 'pre-line' }}>{t('page.caseStudy.approach.title')}</h3>
              </div>
              <div className="flex-1 space-y-0">
                {approaches.map((a, i) => (
                  <div key={a.step} className={`py-5 ${i !== 0 ? 'border-t border-white/15' : ''}`}>
                    <div className="flex items-start gap-4">
                      <span className="flex-shrink-0 text-xs font-black text-white/40 mt-0.5 w-5">{a.step}</span>
                      <div>
                        <p className="text-sm font-semibold text-white leading-snug mb-1">{t(a.titleKey)}</p>
                        <p className="text-xs text-white/60 leading-relaxed">{t(a.descKey)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Results */}
            <div className="relative bg-white p-8 sm:p-10 flex flex-col border border-gray-100">
              <span className="absolute top-4 right-6 text-[10rem] font-black leading-none select-none pointer-events-none text-emerald-50">03</span>
              <div className="mb-8">
                <p className="text-xs font-semibold tracking-[0.2em] text-emerald-600 uppercase mb-3">{t('page.caseStudy.results.eyebrow')}</p>
                <h3 className="text-2xl font-serif text-gray-900 leading-snug" style={{ whiteSpace: 'pre-line' }}>{t('page.caseStudy.results.title')}</h3>
              </div>
              <div className="flex gap-3 mb-8">
                {kpis.map((k) => (
                  <div key={k.valueKey} className="flex-1 text-center border-t-2 border-emerald-500 pt-3">
                    <p className="text-xl font-black text-emerald-600 leading-none">{t(k.valueKey)}</p>
                    <p className="text-[10px] text-gray-400 mt-1 font-medium">{t(k.labelKey)}</p>
                  </div>
                ))}
              </div>
              <div className="flex-1 space-y-0">
                {resultItems.map((r, i) => (
                  <div key={r.titleKey} className={`py-5 ${i !== 0 ? 'border-t border-gray-100' : ''}`}>
                    <div className="flex items-start gap-3">
                      <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      <div>
                        <p className="text-sm font-semibold text-gray-800 leading-snug mb-1">{t(r.titleKey)}</p>
                        <p className="text-xs text-gray-400 leading-relaxed">{t(r.descKey)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-[#255485] text-xs sm:text-sm font-medium tracking-widest mb-3">{t('page.caseStudy.timeline.eyebrow')}</p>
            <h2 className="text-2xl sm:text-3xl font-serif text-gray-900">{t('page.caseStudy.timeline.title')}</h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-0.5 bg-[#255485]/20 sm:-translate-x-0.5"></div>
            <div className="space-y-8 sm:space-y-10">
              {phases.map((phase) => (
                <div key={phase.phaseKey} className={`relative flex flex-col sm:flex-row gap-4 sm:gap-8 ${phase.align === 'right' ? 'sm:flex-row-reverse' : ''}`}>
                  <div className="absolute left-4 sm:left-1/2 w-4 h-4 bg-[#255485] rounded-full border-4 border-white sm:-translate-x-2 top-1"></div>
                  <div className="hidden sm:block sm:w-1/2"></div>
                  <div className={`ml-10 sm:ml-0 sm:w-1/2 ${phase.align === 'right' ? 'sm:pr-10' : 'sm:pl-10'}`}>
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-[#255485] bg-[#255485]/10 px-2 py-0.5 rounded-full">{t(phase.phaseKey)}</span>
                        <span className="text-xs text-gray-400">{t(phase.periodKey)}</span>
                      </div>
                      <h3 className="text-base font-bold text-gray-900 mb-2">{t(phase.titleKey)}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{t(phase.descKey)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Voice */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="text-[#255485] text-xs sm:text-sm font-medium tracking-widest mb-3">{t('page.caseStudy.voice.eyebrow')}</p>
            <h2 className="text-2xl sm:text-3xl font-serif text-gray-900">{t('page.caseStudy.voice.title')}</h2>
          </div>
          <div className="bg-gradient-to-br from-[#255485]/5 to-[#255485]/10 rounded-2xl p-8 sm:p-12 relative">
            <div className="w-10 h-10 flex items-center justify-center text-[#255485]/30 absolute top-6 left-6">
              <i className="ri-double-quotes-l text-5xl"></i>
            </div>
            <blockquote className="text-gray-700 text-base sm:text-lg leading-relaxed text-center pt-4 font-serif">{t('page.caseStudy.voice.quote')}</blockquote>
          </div>
        </div>
      </section>

      {/* Case 2: IT Company */}
      <CaseBlock
        eyebrowKey="page.caseStudy.case2.eyebrow"
        titleKey="page.caseStudy.case2.title"
        subtitleKey="page.caseStudy.case2.subtitle"
        bgColor="#F7F5F2"
        challenges={case2Challenges}
        approaches={case2Approaches}
        results={case2Results}
        kpis={case2Kpis}
      />

      {/* Case 3: Ad Agency */}
      <CaseBlock
        eyebrowKey="page.caseStudy.case3.eyebrow"
        titleKey="page.caseStudy.case3.title"
        subtitleKey="page.caseStudy.case3.subtitle"
        bgColor="#ffffff"
        challenges={case3Challenges}
        approaches={case3Approaches}
        results={case3Results}
        kpis={case3Kpis}
      />

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[#255485]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif text-white mb-4">{t('page.caseStudy.cta.title')}</h2>
          <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-8" style={{ whiteSpace: 'pre-line' }}>{t('page.caseStudy.cta.body')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToContact} className="bg-white text-[#255485] px-8 py-3.5 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2">
              <i className="ri-mail-send-line"></i>{t('page.caseStudy.cta.button1')}
            </button>
            <Link to="/services/consulting" className="bg-transparent text-white border-2 border-white/60 px-8 py-3.5 rounded-lg hover:bg-white/10 transition-colors duration-200 font-medium whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2">
              <i className="ri-arrow-right-line"></i>{t('page.caseStudy.cta.button2')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 mb-12">
            <div>
              <Link to="/" className="flex flex-col leading-none mb-4 cursor-pointer">
                <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.18em' }} className="text-white font-bold text-2xl tracking-widest uppercase">LIKES</span>
                <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.22em' }} className="text-white/70 text-[9px] font-medium tracking-[0.22em] uppercase mt-0.5">Co, Ltd</span>
              </Link>
              <p className="text-white/70 text-sm leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{t('subpage.footer.tagline')}</p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">{t('subpage.footer.nav')}</h3>
              <nav className="space-y-3">
                <Link to="/#vision" className="block text-white/70 hover:text-white text-sm cursor-pointer">{t('nav.vision')}</Link>
                <Link to="/#services" className="block text-white/70 hover:text-white text-sm cursor-pointer">{t('nav.services')}</Link>
                <Link to="/case-study" className="block text-white hover:text-white/80 text-sm cursor-pointer font-medium">{t('nav.caseStudy')}</Link>
                <Link to="/#csr" className="block text-white/70 hover:text-white text-sm cursor-pointer">{t('nav.csr')}</Link>
                <Link to="/#company" className="block text-white/70 hover:text-white text-sm cursor-pointer">{t('nav.company')}</Link>
              </nav>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">{t('subpage.footer.contact')}</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-4">{t('subpage.footer.address')}</p>
              <button onClick={scrollToContact} className="text-white/70 hover:text-white transition-colors duration-200 text-sm underline cursor-pointer">{t('nav.contact')}</button>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/50 text-sm">{t('subpage.footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
